import React, { useState } from 'react'
import { Download, Package, AlertCircle } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

const DownloadZipButton = ({ selectedEvidences, allEvidences, filters = {} }) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)
  const [currentFile, setCurrentFile] = useState('')
  const [error, setError] = useState(null)
  const { token } = useAuthStore()

  const handleDownloadZip = async () => {
    if (!selectedEvidences || selectedEvidences.length === 0) {
      setError('No hay evidencias seleccionadas para descargar')
      return
    }

    setIsDownloading(true)
    setDownloadProgress(0)
    setCurrentFile('')
    setError(null)

    try {
      console.log('📦 Descargando archivos para:', selectedEvidences.length, 'evidencias')
      console.log('📋 Evidencias seleccionadas (IDs):', selectedEvidences)
      console.log('📋 Todas las evidencias disponibles:', allEvidences)

      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''
      
      // Filtrar las evidencias seleccionadas del array completo
      const evidencesToDownload = allEvidences.filter(evidence => 
        selectedEvidences.includes(evidence.id)
      )

      console.log('📋 Evidencias filtradas para descargar:', evidencesToDownload)

      if (evidencesToDownload.length === 0) {
        setError('No se encontraron evidencias para descargar')
        setIsDownloading(false)
        return
      }

      // Filtrar solo evidencias que tienen archivos (no solo texto de evidencia faltante)
      const evidencesWithFiles = evidencesToDownload.filter(evidence => 
        evidence.url_archivo && evidence.url_archivo.trim() !== ''
      )

      console.log('📋 Evidencias con archivos:', evidencesWithFiles.length)

      if (evidencesWithFiles.length === 0) {
        setError('Las evidencias seleccionadas no tienen archivos para descargar')
        setIsDownloading(false)
        return
      }

      console.log('📋 Iniciando descarga de archivos individuales y generación de ZIP')

      const zip = new window.JSZip()
      let completed = 0
      let failed = 0

      for (const evidence of evidencesWithFiles) {
        const fileName = evidence.nombre_archivo || 'archivo_sin_nombre'
        setCurrentFile(fileName)
        setDownloadProgress(Math.round((completed / evidencesWithFiles.length) * 100))

        try {
          console.log(`📥 Procesando: ${fileName}, URL: ${evidence.url_archivo}`)

          // Obtener URL segura para el archivo
          const secureUrlResponse = await fetch(
            `${baseUrl}/api/evidences/secure-url?fileName=${encodeURIComponent(evidence.url_archivo)}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          )

          if (!secureUrlResponse.ok) {
            const errorText = await secureUrlResponse.text()
            console.warn(`⚠️ No se pudo obtener URL segura para ${fileName}: ${secureUrlResponse.status} - ${errorText}`)
            failed++
            completed++
            continue
          }

          const { url: downloadUrl } = await secureUrlResponse.json()
          console.log(`🔗 URL de descarga obtenida para ${fileName}`)

          // Descargar el archivo
          const fileResponse = await fetch(downloadUrl)
          if (!fileResponse.ok) {
            console.warn(`⚠️ No se pudo descargar ${fileName}: ${fileResponse.status}`)
            failed++
            completed++
            continue
          }

          const blob = await fileResponse.blob()
          console.log(`✅ Archivo descargado: ${fileName} (${blob.size} bytes)`)
          
          // Crear estructura de carpetas en el ZIP incluyendo el año
          const year = evidence.año || evidence.year || new Date().getFullYear()
          const folderPath = `${evidence.programa}/${evidence.dimension}/${evidence.criterio}/${year}/`
          zip.file(folderPath + fileName, blob)
          
          completed++
        } catch (fileErr) {
          console.error(`❌ Error al descargar ${fileName}:`, fileErr)
          failed++
          completed++
        }
      }

      if (completed === failed) {
        throw new Error('No se pudo descargar ningún archivo')
      }

      setCurrentFile('Generando archivo ZIP...')
      setDownloadProgress(100)

      const zipBlob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
        if (metadata.percent) {
          setCurrentFile(`Comprimiendo: ${metadata.percent.toFixed(0)}%`)
        }
      })

      const url = window.URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `evidencias_${new Date().toISOString().split('T')[0]}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      console.log(`✅ ZIP descargado exitosamente (${completed - failed} archivos, ${failed} fallidos)`)

      if (failed > 0) {
        setError(`Se descargaron ${completed - failed} archivos. ${failed} archivos fallaron.`)
      }

    } catch (err) {
      console.error('❌ Error en el proceso de descarga ZIP:', err)
      setError(err.message)
    } finally {
      setIsDownloading(false)
      setDownloadProgress(0)
      setCurrentFile('')
    }
  }

  const selectedCount = selectedEvidences ? selectedEvidences.length : 0

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={handleDownloadZip}
        disabled={isDownloading || selectedCount === 0}
        className={`
          inline-flex items-center px-4 py-2 rounded-lg font-medium transition-colors
          ${selectedCount === 0 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
            : isDownloading
              ? 'bg-blue-100 text-blue-600 cursor-wait'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }
        `}
      >
        {isDownloading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
            {currentFile ? `Descargando: ${currentFile}` : `Generando ZIP (${downloadProgress}%)...`}
          </>
        ) : (
          <>
            <Package className="h-4 w-4 mr-2" />
            Descargar ZIP ({selectedCount})
          </>
        )}
      </button>

      {error && (
        <div className="flex items-center text-red-600 text-sm bg-red-50 p-2 rounded">
          <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {selectedCount === 0 && (
        <p className="text-sm text-gray-500">
          Selecciona evidencias para descargar en ZIP
        </p>
      )}
    </div>
  )
}

export default DownloadZipButton